/**
 * Helpers for a listing's condition — new vs refurbished, and how good a
 * refurbished unit is.
 *
 * eBay and Back Market grade their stock in different words, so the API maps both
 * onto one normalised ladder (see ConditionGrade) and keeps the retailer's own
 * wording in `condition_label`. Display prefers the retailer's wording, because
 * "Excellent - Refurbished" is what the buyer will see when they follow the link;
 * the normalised grade is what sorting and comparison use.
 */

import {
  CONDITION_GRADE_BLURB,
  CONDITION_GRADE_ORDER,
  type ConditionGrade,
  type Seller,
} from "./types";

/**
 * Whether a listing is for a used machine.
 *
 * Listings scraped before refurbished sellers existed have no `condition` at all,
 * so anything that isn't explicitly refurbished is new.
 */
export function isRefurbished(seller: Pick<Seller, "condition">): boolean {
  return seller.condition === "refurbished";
}

/** The grade to sort/compare on, defaulting ungraded refurbished stock to worst. */
export function conditionGrade(seller: Pick<Seller, "condition_grade">): ConditionGrade {
  const grade = seller.condition_grade;
  return grade && CONDITION_GRADE_ORDER.includes(grade) ? grade : "unspecified";
}

/**
 * Sort key: new listings first, then refurbished best-graded first.
 *
 * A refurbished unit is cheaper because it is used, so it would otherwise win any
 * price-ordered list and read as the default option. New goes first unless the
 * person asked for refurbished specifically.
 */
export function conditionRank(seller: Pick<Seller, "condition" | "condition_grade">): number {
  if (!isRefurbished(seller)) return -1;
  return CONDITION_GRADE_ORDER.indexOf(conditionGrade(seller));
}

/** Badge text for a refurbished listing, e.g. "Refurbished · Excellent". */
export function conditionBadge(
  seller: Pick<Seller, "condition" | "condition_grade" | "condition_label">,
): string | null {
  if (!isRefurbished(seller)) return null;

  // Prefer the retailer's own wording — it already says "Refurbished" on eBay
  // ("Excellent - Refurbished"), so only Back Market's bare grade needs the prefix.
  const label = seller.condition_label?.trim();
  if (label) {
    return /refurb/i.test(label) ? label : `Refurbished · ${label}`;
  }

  const grade = conditionGrade(seller);
  return grade === "unspecified" ? "Refurbished" : `Refurbished · ${grade}`;
}

/** What the grade means, for a title attribute / tooltip on the badge. */
export function conditionTooltip(
  seller: Pick<Seller, "condition" | "condition_grade" | "condition_description">,
): string | null {
  if (!isRefurbished(seller)) return null;
  return seller.condition_description?.trim() || CONDITION_GRADE_BLURB[conditionGrade(seller)];
}
